import { Link, RouteComponentProps, useHistory } from "react-router-dom";
import styled from "styled-components";
import { isEmpty } from "lodash";
import Get from "../../../components/Get";
import { VehicleType } from "../types";
import VehicleInfo from "../components/VehicleInfo";
import {
  isAdministrationField,
  isDetailsField,
  isSeizureField,
  isTelematicsField,
  isAssetField,
  isKiwaCertificateFields,
  detailFields,
  isVbvTrackingFields,
  isRentalReportFields,
  isRecoveryReportFields,
} from "../fields";
import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "../../users/authSlice";
import useRouteGuard from "../../../hooks/useRouteGuard";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
  Paper,
  Snackbar,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import { camelToString, fields } from "../../vin-search/VinSearchItem";
import RDWTables from "../../rdw/components/RDWTables";
import UpdateLog from "../components/UpdateLog";
import GroupFields from "../../../components/GroupFields";
import InternalNotes from "../components/InternalNotes";
import LocationStatus from "../components/LocationStatus";
import StellantisFleetRegistration from "../components/StellantisFleetRegistration";
import { useEffect, useState } from "react";
import api from "../../../utils/api";
import { setIsPageLoading } from "../../../appSlice";
import env from "../../../constants/env";
import StophelingSection from "../components/StophelingSection";
import ANPRSection from "../components/ANPRSection";
import VehicleStellantisEvents from "../../stellantis/VehicleStellantisEvents";
import React from "react";
import ErrorBoundary from "../../../components/ErrorBoundary";

// Makes tracked via the Toyota Connected fleet API. Lexus is a Toyota brand
// on the same fleet, so it shares the Location Status panel.
const TOYOTA_FLEET_MAKES = ["TOYOTA", "LEXUS"];

const STELLANTIS_BRANDS = [
  "PEUGEOT",
  "CITROEN",
  "DS",
  "OPEL",
  "VAUXHALL",
  "FIAT",
  "ALFA ROMEO",
  "JEEP",
  "RAM",
  "DODGE",
  "CHRYSLER",
  "MASERATI",
  "LANCIA",
  "ABARTH",
];

const S = {
  Page: styled.div`
    margin: auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
  `,
  Buttons: styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 16px;
    @media print {
      display: none;
    }
    @media screen and (max-width: 900px) {
      margin-top: 16px;
      justify-content: flex-start;
    }
  `,
  ButtonLink: styled(Link)`
    font-size: 12px;
  `,
  ColWrapper: styled.div`
    display: flex;
    flex-wrap: wrap;
  `,
  Col: styled.div`
    padding: 24px;
    width: 50%;
    @media screen and (max-width: 900px) {
      width: 100%;
    }
  `,
  Wrapper: styled.div`
    position: relative;
    background: white;
    z-index: 9;
    margin: 8px;
    padding: 8px;
    border-radius: 4px;
  `,
  Td: styled.td`
    width: 50%;
    text-transform: capitalize;
  `,
};

export const sortByKey = (a, b) => {
  if (a.key < b.key) {
    return -1;
  }
  if (a.key > b.key) {
    return 1;
  }
  return 0;
};

const RDW = (props: { plate: string }) => {
  const plate = (props.plate || "-").toUpperCase();
  return (
    <ErrorBoundary>
      <Get url={`/rdw/${plate}/`} defaultErrorMessage="Plate not found">
        {(props) => <RDWTables {...props} />}
      </Get>
    </ErrorBoundary>
  );
};

const PreviewDialog = ({
  open,
  onClose,
  emailData,
  loading,
  sendEndpoint,
  error,
  onPreviewSpareKeyEmail,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(
    env.isKinto ? "Germany" : "Netherlands",
  );

  const handleCountryChange = async (event: SelectChangeEvent) => {
    const newCountry = event.target.value;
    setSelectedCountry(newCountry);
    await onPreviewSpareKeyEmail(newCountry);
  };

  const getPreviewText = () => {
    if (!emailData?.textContent) return "";
    if (sendEndpoint === "send-spare-key-email/") {
      return emailData.textContent.replace(/COUNTRY/g, selectedCountry);
    }
    return emailData.textContent;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSuccess(false);

    const response = await api.post(
      `/vehicles/${emailData.vehicleId}/${sendEndpoint}`,
      env.isFleet ? { location: selectedCountry } : {},
    );

    setIsSubmitting(false);

    if (response.error) {
      console.error("Failed to send email:", response.error);
      return;
    }

    setSuccess(true);
    setTimeout(() => {
      onClose();
      setSuccess(false);
    }, 2000);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{emailData?.subject || "Email Preview"}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 4,
            }}
          >
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading preview...</Typography>
          </Box>
        ) : error ? (
          <Typography
            sx={{
              color: "#d32f2f",
              fontSize: "0.9rem",
              padding: "16px",
              backgroundColor: "#ffebee",
              borderRadius: "4px",
              border: "1px solid #ffcdd2",
            }}
          >
            {error}
          </Typography>
        ) : (
          <>
            {sendEndpoint === "send-spare-key-email/" && !env.isKinto && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Country</InputLabel>
                <Select
                  value={selectedCountry}
                  label="Country"
                  onChange={handleCountryChange}
                >
                  <MenuItem value="Netherlands">Netherlands</MenuItem>
                  <MenuItem value="Hungary">Hungary</MenuItem>
                  <MenuItem value="Serbia">Serbia</MenuItem>
                </Select>
              </FormControl>
            )}
            <Typography
              sx={{
                whiteSpace: "pre-wrap",
                fontFamily: "monospace",
                fontSize: "0.9rem",
                padding: "16px",
                backgroundColor: "#f5f5f5",
                borderRadius: "4px",
              }}
            >
              {getPreviewText()}
            </Typography>
          </>
        )}
        {success && (
          <Typography
            sx={{
              color: "#2e7d32",
              fontSize: "0.9rem",
              padding: "16px",
              backgroundColor: "#e8f5e9",
              borderRadius: "4px",
              border: "1px solid #c8e6c9",
              mt: 2,
            }}
          >
            Email sent successfully!
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {!error && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={isSubmitting || loading}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting ? "Sending..." : "Send Email"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const VehicleView = (props: RouteComponentProps<{ id: string }>) => {
  useRouteGuard(
    ["canReadVehicles", "canReportRental"],
    ["canViewDatabase", "canViewRentalReport"],
  );
  const user = useSelector(selectUser);
  const canEdit =
    user.isSuperuser || user.canEditVehicles || user.canReportRental;
  const id = props.match.params.id;
  const [data, setData] = useState<VehicleType | undefined>(undefined);
  const [histories, setHistories] = useState<VehicleType["history"]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [emailPreviewData, setEmailPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [sendEndpoint, setSendEndpoint] = useState("send-location-email/");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const mounted = React.useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const handlePreviewEmail = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setSendEndpoint("send-location-email/");
    setPreviewError(null);

    const response = await api.post(`/vehicles/${id}/preview-location-email/`);

    if (!mounted.current) return;

    setPreviewLoading(false);

    const error = (response.error as any)?.error || response.error;
    if (error) {
      console.error("Failed to send email:", error);
      setPreviewError(
        typeof error === "string"
          ? error
          : "Failed to send email. No country or GPS data found.",
      );
      return;
    }

    setEmailPreviewData({
      ...response.data,
      vehicleId: id,
    });
  };

  const handlePreviewSpareKeyEmail = async (country: string) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setSendEndpoint("send-spare-key-email/");
    setPreviewError(null);

    const response = await api.post(
      `/vehicles/${id}/preview-spare-key-email/`,
      {
        location: country,
      },
    );

    if (!mounted.current) return;

    setPreviewLoading(false);

    const error = (response.error as any)?.error || response.error;
    if (error) {
      console.error("Failed to send email:", error);
      setPreviewError(
        typeof error === "string" ? error : "Failed to send email.",
      );
      return;
    }

    setEmailPreviewData({
      ...response.data,
      vehicleId: id,
    });
  };

  const handlePreviewMissingCarEmail = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setSendEndpoint("send-missing-car-email/");
    setPreviewError(null);

    const response = await api.post(
      `/vehicles/${id}/preview-missing-car-email/`,
    );

    if (!mounted.current) return;

    setPreviewLoading(false);

    const error = (response.error as any)?.error || response.error;
    if (error) {
      console.error("Failed to send email:", error);
      setPreviewError(
        typeof error === "string"
          ? error
          : "Failed to send email. Please check if all required fields are filled.",
      );
      return;
    }

    setEmailPreviewData({
      ...response.data,
      vehicleId: id,
    });
  };

  const handleDeleteCase = async () => {
    setIsDeleting(true);
    const response = await api.delete(`/vehicles/${id}/`);
    if (!mounted.current) return;
    setIsDeleting(false);

    if (response.error) {
      console.error("Failed to delete case:", response.error);
      setDeleteDialogOpen(false);
      return;
    }

    history.push("/vehicles");
  };

  const fetchData = async () => {
    try {
      dispatch(setIsPageLoading(false));
      const vehicleResp = await api.get(`/vehicles/${id}/`);
      const data = vehicleResp.data;
      const rentalReportData = data.rentalReport || {
        theftReport: [],
        insuranceDocs: [],
        vehicleRegistrationPapers: [],
        authorizationToTowDocs: [],
        powerOfAttorney: [],
        rentalAgreement: [],
        renterIdDocs: [],
        rentalAgreementCancellationDocs: [],
        supportingDocs: [],
      };

      if (!mounted.current) return;

      setData({
        ...vehicleResp.data,
        theftReport: [...data.theftReport, ...rentalReportData.theftReport],
        insuranceDocs: [
          ...data.insuranceDocs,
          ...rentalReportData.insuranceDocs,
        ],
        vehicleRegistrationPapers: [
          ...data.vehicleRegistrationPapers,
          ...rentalReportData.vehicleRegistrationPapers,
        ],
        authorizationToTowDocs: [
          ...data.authorizationToTowDocs,
          ...rentalReportData.authorizationToTowDocs,
        ],
        powerOfAttorney: rentalReportData.powerOfAttorney || [],
        rentalAgreement: rentalReportData.rentalAgreement || [],
        renterIdDocs: rentalReportData.renterIdDocs || [],
        rentalAgreementCancellationDocs:
          rentalReportData.rentalAgreementCancellationDocs || [],
        supportingDocs: rentalReportData.supportingDocs || [],
      });
      const historiesResp = await api.get(`/vehicles/${id}/histories/`);
      if (!mounted.current) return;
      setHistories(historiesResp.data);
    } catch (err) {
      console.log(err);
    } finally {
      if (mounted.current) {
        dispatch(setIsPageLoading(false));
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const getCoordinates = (lastLocatedAt: string) => {
    if (!lastLocatedAt) return null;
    const [lat, long] = lastLocatedAt
      .split("/")
      .map((coord) => parseFloat(coord.trim()));
    if (isNaN(lat) || isNaN(long)) return null;
    return { lat, long };
  };

  return (
    <ErrorBoundary>
      <S.Page>
        {data && (
          <>
            <Box sx={{ padding: "24px" }}>
              <Grid
                container
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="h4">SMVIU Data</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <S.Buttons>
                    {data.hasLocation &&
                      (user.canViewMap || user.isSuperuser) && (
                        <S.ButtonLink
                          to={`/map?vehicleId=${data.id}&mode=detail`}
                        >
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                          >
                            View on Map
                          </Button>
                        </S.ButtonLink>
                      )}
                    {canEdit && (
                      <>
                        <S.ButtonLink to={`/vehicles/${id}/edit/`}>
                          <Button variant="outlined" size="small">
                            Edit
                          </Button>
                        </S.ButtonLink>
                        <Button
                          variant="outlined"
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialogOpen(true)}
                        >
                          Delete Case
                        </Button>
                      </>
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handlePreviewEmail}
                      sx={{ mr: 1 }}
                    >
                      Request to NCB
                    </Button>
                    {env.isFleet && (
                      <>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() =>
                            handlePreviewSpareKeyEmail(
                              env.isKinto ? "Germany" : "Netherlands",
                            )
                          }
                          sx={{ mr: 1 }}
                        >
                          Request Spare Key
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handlePreviewMissingCarEmail}
                          sx={{ mr: 1 }}
                        >
                          Missing Car Report
                        </Button>
                      </>
                    )}
                    {env.isKinto && (
                      <S.ButtonLink to={`/vehicles/${id}/telematics-report/`}>
                        <Button variant="outlined" size="small">
                          Telematics Report
                        </Button>
                      </S.ButtonLink>
                    )}

                    <S.ButtonLink to={`/vehicles/${id}/seizure-report/`}>
                      <Button variant="outlined" size="small">
                        Seizure Report
                      </Button>
                    </S.ButtonLink>

                    <S.ButtonLink to={`/vehicles/${id}/vehicle-report/`}>
                      <Button variant="outlined" size="small">
                        Vehicle Report
                      </Button>
                    </S.ButtonLink>
                  </S.Buttons>
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="stretch">
                <Grid size={{ xs: 12, lg: 6 }}>
                  <Paper sx={{ p: 2, height: "100%" }}>
                    <ErrorBoundary>
                      <VehicleInfo
                        header="Vehicle Details"
                        fieldFilter={isDetailsField}
                        vehicle={data}
                        compact
                        fieldSorting={(a, b) => {
                          return (
                            // @ts-ignore
                            detailFields.indexOf(a.key) -
                            // @ts-ignore
                            detailFields.indexOf(b.key)
                          );
                        }}
                      />
                    </ErrorBoundary>

                    <ErrorBoundary>
                      <VehicleInfo
                        header="Files"
                        fieldFilter={isAssetField}
                        vehicle={data}
                        compact
                      />
                    </ErrorBoundary>

                    <ErrorBoundary>
                      <VehicleInfo
                        header="Telematics / GPS Data"
                        fieldFilter={isTelematicsField}
                        vehicle={{
                          ...data,
                          ...data.kiwaCertificate,
                          scmClass: !isEmpty(data.kiwaCertificate),
                        }}
                        compact
                        actionsRender={() => (
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              alignItems: "center",
                            }}
                          >
                            {data.hasLocation &&
                            (user.canViewMap || user.isSuperuser) ? (
                              <Button variant="outlined">
                                <Link
                                  to={`/map?vehicleId=${data.id}&mode=detail`}
                                >
                                  Map
                                </Link>
                              </Button>
                            ) : null}
                            {data.tracker &&
                            (user.canReadVehicles ||
                              user.isSuperuser ||
                              user.canReportRental) ? (
                              <Button variant="outlined">
                                <Link to={`/trackers?imei=${data.tracker}`}>
                                  Tracker
                                </Link>
                              </Button>
                            ) : null}
                          </div>
                        )}
                      />
                    </ErrorBoundary>

                    {!isEmpty(data.kiwaCertificate) && (
                      <ErrorBoundary>
                        <VehicleInfo
                          header="SCM Class"
                          fieldFilter={isKiwaCertificateFields}
                          vehicle={{ ...data, ...data.kiwaCertificate }}
                          compact
                        />
                      </ErrorBoundary>
                    )}

                    {!isEmpty(data.vbvTrackingLog) && (
                      <ErrorBoundary>
                        <VehicleInfo
                          header="Vbv Sent Updates"
                          fieldFilter={isVbvTrackingFields}
                          vehicle={{ ...data, ...data.vbvTrackingLog }}
                          compact
                        />
                      </ErrorBoundary>
                    )}

                    {!isEmpty(data.rentalReport) && (
                      <ErrorBoundary>
                        <VehicleInfo
                          header="Rental Report"
                          fieldFilter={isRentalReportFields}
                          vehicle={{ ...data.rentalReport }}
                          compact
                        />
                      </ErrorBoundary>
                    )}
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, lg: 6 }}>
                  <Paper sx={{ p: 2, height: "100%" }}>
                    <ErrorBoundary>
                      <InternalNotes vehicleId={id} />
                    </ErrorBoundary>

                    <ErrorBoundary>
                      <VehicleInfo
                        header="Seizure Details"
                        fieldFilter={isSeizureField}
                        vehicle={data}
                        compact
                      />
                    </ErrorBoundary>

                    <ErrorBoundary>
                      <VehicleInfo
                        header="Recovery report"
                        fieldFilter={isRecoveryReportFields}
                        vehicle={data}
                        compact
                      />
                    </ErrorBoundary>

                    <ErrorBoundary>
                      <VehicleInfo
                        fieldSorting={sortByKey}
                        header="Admin Details"
                        fieldFilter={isAdministrationField}
                        vehicle={data}
                        compact
                      />
                    </ErrorBoundary>

                    <ErrorBoundary>
                      <UpdateLog logs={histories} />
                    </ErrorBoundary>
                  </Paper>
                </Grid>

                {TOYOTA_FLEET_MAKES.includes(
                  data.make?.toUpperCase() || "",
                ) && (
                  <Grid size={12}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h5" gutterBottom>
                        Location Status
                      </Typography>
                      <ErrorBoundary>
                        <LocationStatus
                          vehicleId={data.id}
                          vehiclePlate={data.plate}
                          stellantisEligibility={data.stellantisEligibility}
                          stellantisContract={data.stellantisContract}
                          isSuperuser={user.isSuperuser}
                          onUpdate={fetchData}
                        />
                      </ErrorBoundary>
                    </Paper>
                  </Grid>
                )}

                {STELLANTIS_BRANDS.includes(data.make?.toUpperCase() || "") && (
                  <Grid size={12}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h5" gutterBottom>
                        Tracking — Stellantis Location Services
                      </Typography>
                      <ErrorBoundary>
                        <StellantisFleetRegistration
                          vehicleId={data.id}
                          eligibility={data.stellantisEligibility}
                          contract={data.stellantisContract}
                          onUpdate={fetchData}
                        />
                      </ErrorBoundary>
                    </Paper>
                  </Grid>
                )}

                {(STELLANTIS_BRANDS.includes(data.make?.toUpperCase() || "") ||
                  data.stellantisEligibility ||
                  data.stellantisContract) &&
                  user.isSuperuser && (
                    <Grid size={12}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="h5" gutterBottom>
                          Stellantis Events
                        </Typography>
                        <ErrorBoundary>
                          <VehicleStellantisEvents vehicleId={data.id} />
                        </ErrorBoundary>
                      </Paper>
                    </Grid>
                  )}
              </Grid>

              {data.webData && (
                <Paper sx={{ p: 2, my: 2 }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <ErrorBoundary>
                        <GroupFields
                          data={data.webData}
                          settings={[
                            {
                              group: "Web hits",
                              fields: fields.map((field) => ({
                                field,
                                label: camelToString(field),
                              })),
                            },
                          ]}
                        />
                      </ErrorBoundary>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              <Divider style={{ marginBottom: 20, marginTop: 36 }} />
            </Box>
            {!env.isFleet && (
              <Box style={{ padding: 24 }}>
                <Typography variant="h4" gutterBottom>
                  RDW Data
                </Typography>
                <ErrorBoundary>
                  <RDW plate={data.plate} />
                </ErrorBoundary>
              </Box>
            )}
            {data?.helingRaw && (
              <ErrorBoundary>
                <StophelingSection
                  vehicleId={id}
                  data={{
                    helingRaw: data.helingRaw,
                    helingStatus: data.helingStatus,
                    helingLastChecked: data.helingLastChecked,
                  }}
                />
              </ErrorBoundary>
            )}
            {data?.anprHit && data.anprHits && data.anprHits.length > 0 && (
              <Grid size={12}>
                <ErrorBoundary>
                  <ANPRSection hits={data.anprHits} />
                </ErrorBoundary>
              </Grid>
            )}
            <Dialog
              open={deleteDialogOpen}
              onClose={() => setDeleteDialogOpen(false)}
            >
              <DialogTitle>Delete Case</DialogTitle>
              <DialogContent>
                <Typography>
                  Are you sure you want to delete this case? This action cannot
                  be undone.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteCase}
                  variant="contained"
                  color="error"
                  disabled={isDeleting}
                  startIcon={isDeleting ? <CircularProgress size={20} /> : null}
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </Button>
              </DialogActions>
            </Dialog>
            <PreviewDialog
              open={previewOpen}
              onClose={() => {
                setPreviewOpen(false);
                setEmailPreviewData(null);
                setPreviewError(null);
              }}
              emailData={emailPreviewData}
              loading={previewLoading}
              sendEndpoint={sendEndpoint}
              error={previewError}
              onPreviewSpareKeyEmail={handlePreviewSpareKeyEmail}
            />
          </>
        )}
      </S.Page>
    </ErrorBoundary>
  );
};

export default VehicleView;
